import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";
import { getMemberInfo } from "@/hooks/useMembers";
import { membersService } from "@/services/members.service";
import type { MemberInvite } from "@/types/member";

const InviteMember: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const member = location.state?.targetMember || {};
  const backUrl = location.state?.backUrl;

  const [loading, setLoading] = React.useState(false);
  const [invite, setInvite] = React.useState<MemberInvite | null>(null);

  const memberId = member.id || member._id;

  React.useEffect(() => {
    if (!memberId) return;
    const familyId = memberInfo?.current_family || "";
    membersService
      .getMemberInvite(memberId, familyId)
      .then(setInvite)
      .catch(() => setInvite(null));
  }, [memberId]);

  // Contact display — sourced from the invite record, not the member profile
  let contact = "";
  let mode: "mobile" | "email" | undefined;
  if (invite?.mobile) {
    const prefix = invite.dialCode ? `${invite.dialCode}-` : "";
    contact = `${prefix}${invite.mobile.slice(-8).padStart(8, "x")}`;
    mode = "mobile";
  } else if (invite?.email) {
    contact = invite.email;
    mode = "email";
  }

  const handleConfirmInvite = async () => {
    if (!mode) return;
    setLoading(true);
    try {
      await membersService.inviteMember(memberId, mode, {
        email: invite?.email,
        mobile: invite?.mobile,
      });
      navigate("/profile/family/invite-success", {
        state: {
          memberName: member.first_name || member.firstName || "",
          backUrl,
        },
      });
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditContact = () => {
    navigate("/profile/family/invite-contact-method", {
      state: { targetMember: member, backUrl },
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="text-2xl font-semibold text-neutral-900 text-left w-full mb-12 mt-8">
          Should we send an invite to{" "}
          {member.first_name || member.firstName || ""} on {contact}?
        </div>
        <div className="w-full flex flex-col gap-4 mt-8">
          {mode && (
            <MButton
              variant="filled"
              size="lg"
              state={loading ? "disabled" : "default"}
              className="w-full"
              disabled={loading}
              onClick={handleConfirmInvite}
            >
              {loading ? "Inviting..." : "Confirm"}
            </MButton>
          )}
          <MButton
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleEditContact}
          >
            Edit contact
          </MButton>
          <MButton
            variant="outline"
            size="lg"
            state="default"
            className="w-full"
            onClick={() => navigate(-1)}
          >
            Cancel
          </MButton>
        </div>
      </div>
    </div>
  );
};

export default InviteMember;
